import { useEffect, useMemo } from "react";
import { useForm, FormProvider, FieldValues, UseFormReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/class-control";
import { DynamicFormField } from "./dynamic-form-field";
import { buildSchema, extractDefaultValues } from "./schema-builder";
import { FormConfig, FieldConfig, FormSection } from "./types";
import { HeaderForm } from "../header-form";
import { Form } from "../ui/form";

/**
 * FormBuilder - Componente principal para criação declarativa de formulários
 *
 * @example
 * ```tsx
 * <FormBuilder
 *   fields={[
 *     { name: "email", type: "email", label: "E-mail", required: true },
 *     { name: "password", type: "password", label: "Senha", required: true },
 *   ]}
 *   onSubmit={handleLogin}
 *   submitLabel="Entrar"
 * />
 * ```
 */
export function FormBuilder<TData extends FieldValues = FieldValues>({
  fields,
  layout,
  onSubmit,
  onError,
  onChange,
  submitLabel = "Salvar",
  submitPosition = "right",
  showReset = false,
  showSave = true,
  resetLabel = "Limpar",
  isLoading = false,
  disabled = false,
  mode = "onSubmit",
  defaultValues,
  schema: customSchema,
  className,
  formClassName,
  formId,
  formInstance,
}: FormConfig<TData>) {
  // Gerar schema automaticamente ou usar customizado
  const schema = useMemo(
    () => customSchema || buildSchema(fields),
    [fields, customSchema]
  );

  // Extrair valores default dos fields
  const fieldsDefaultValues = useMemo(
    () => extractDefaultValues(fields),
    [fields]
  );

  // Inicializar form
  const internalForm = useForm({
    resolver: zodResolver(schema),
    defaultValues: { ...fieldsDefaultValues, ...defaultValues } as Record<string, unknown>,
    mode,
  });

  const form = (formInstance ?? internalForm) as UseFormReturn<TData>;

  // Watch para onChange callback
  useEffect(() => {
    if (!onChange) return;

    const subscription = form.watch((value) => {
      onChange(value as TData);
    });

    return () => subscription.unsubscribe();
  }, [form, onChange]);

  // Handler de submit
  const handleSubmit = form.handleSubmit(
    async (data) => {
      try {
        await onSubmit(data as TData);
      } catch (error) {
        console.error("Erro no submit do formulário:", error);
      }
    },
    (errors) => {
      onError?.(errors);
    }
  );

  // Calcular classes do grid
  const gridColumns = layout?.columns || 2;
  const gridGap = {
    sm: "gap-3",
    md: "gap-4",
    lg: "gap-6",
  }[layout?.gap || "md"];

  const gridClasses = cn(
    "grid",
    gridGap,
    {
      "grid-cols-1": gridColumns === 1,
      "grid-cols-1 md:grid-cols-2":
        gridColumns === 2 && layout?.responsive !== false,
      "grid-cols-2": gridColumns === 2 && layout?.responsive === false,
      "grid-cols-1 md:grid-cols-2 lg:grid-cols-3": gridColumns === 3,
      "grid-cols-1 md:grid-cols-2 lg:grid-cols-4": gridColumns === 4,
    }
  );

  // Renderizar campos com colSpan
  const renderFields = (fieldsToRender: FieldConfig[] = fields) => {
    return fieldsToRender.map((fieldConfig) => {
      const colSpanClass = getColSpanClass(fieldConfig.colSpan, gridColumns);

      return (
        <div key={fieldConfig.name} className={colSpanClass}>
          <DynamicFormField config={fieldConfig} />
        </div>
      );
    });
  };

  // Renderizar por seções se configurado
  const renderSections = () => {
    if (!layout?.sections) return renderFields();

    return layout.sections.map((section, index) => {
      const sectionFields = fields.filter((f) =>
        section.fields.includes(f.name)
      );

      return (
        <SectionWrapper key={index} section={section}>
          <div className={gridClasses}>{renderFields(sectionFields)}</div>
        </SectionWrapper>
      );
    });
  };

  // Classes do container de botões
  const submitContainerClasses = cn("mt-6 flex gap-2", {
    "justify-start": submitPosition === "left",
    "justify-center": submitPosition === "center",
    "justify-end": submitPosition === "right",
    "w-full": submitPosition === "full",
  });

  const isFormDisabled = disabled || isLoading;

  return (
    <Form {...form}>
      <form
        id={formId}
        onSubmit={handleSubmit}
        className={cn("space-y-6", formClassName)}
      >
        <div className={className}>
          {layout?.sections ? (
            <div className="space-y-8">{renderSections()}</div>
          ) : (
            <div className={gridClasses}>{renderFields()}</div>
          )}
        </div>

        <div className={submitContainerClasses}>
          {showReset && (
            <Button
              type="button"
              variant="outline"
              onClick={() => form.reset()}
              disabled={isFormDisabled}
            >
              {resetLabel}
            </Button>
          )}

          {showSave && (
            <Button
              type="submit"
              disabled={isFormDisabled}
              className={cn(submitPosition === "full" && "w-full")}
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {submitLabel}
            </Button>
          )}
        </div>
      </form>
    </Form>
  );
}

/**
 * Wrapper para seções do formulário
 */
function SectionWrapper({
  section,
  children,
}: {
  section: FormSection;
  children: React.ReactNode;
}) {
  // TODO: Implementar collapsible se necessário
  return (
    <div className="space-y-4">
      {(section.title || section.description) && (
        <div className="">
          {section.title && (
            <div className="flex items-center w-full">
              <HeaderForm title={section.title} />
            </div>
          )}
          {section.description && (
            <p className="text-sm text-muted-foreground">{section.description}</p>
          )}
        </div>
      )}
      {children}
    </div>
  );
}

/**
 * Calcula a classe de colSpan baseado na configuração
 */
function getColSpanClass(
  colSpan: FieldConfig["colSpan"],
  gridColumns: number
): string {
  if (!colSpan || colSpan === 1) return "";
  if (colSpan === "full") return "col-span-full";

  const colSpanMap: Record<number, string> = {
    2: "md:col-span-2",
    3: "md:col-span-3",
    4: "md:col-span-4",
  };

  return colSpanMap[colSpan] || "";
}

// Re-export types para conveniência
export type { FormConfig, FieldConfig, FormSection, FormLayout } from "./types";
